/**
 * Feedback Resource for collecting user feedback
 */

import type { ZapSEA } from '../client';
import { FeedbackRequest, FeedbackResponse } from '../types';

export class FeedbackResource {
  constructor(private client: ZapSEA) {}

  /**
   * Submit user feedback
   * 
   * @example
   * ```typescript
   * const response = await client.feedback.submit({
   *   page: 'impact-simulation',
   *   rating: 5,
   *   comment: 'Great feature! Very helpful for policy analysis.',
   *   feedbackType: 'general',
   *   category: 'usability'
   * });
   * 
   * console.log('Feedback submitted:', response.feedbackId);
   * ```
   */
  async submit(request: FeedbackRequest): Promise<FeedbackResponse> {
    this._validateFeedbackRequest(request);
    return this.client.request<FeedbackResponse>('POST', '/v2/feedback', request);
  }

  /**
   * Submit bug report
   * 
   * @example
   * ```typescript
   * const response = await client.feedback.reportBug({
   *   page: 'dashboard',
   *   comment: 'Charts not loading properly on mobile devices',
   *   apiEndpoint: '/v2/impact/simulate',
   *   sessionId: 'session_123'
   * });
   * ```
   */
  async reportBug(request: {
    page: string;
    comment: string;
    apiEndpoint?: string;
    sessionId?: string;
    customerEmail?: string;
  }): Promise<FeedbackResponse> {
    const feedbackRequest: FeedbackRequest = {
      ...request,
      feedbackType: 'bug_report',
      category: 'technical'
    };

    return this.submit(feedbackRequest);
  }

  /**
   * Submit feature request
   * 
   * @example
   * ```typescript
   * const response = await client.feedback.requestFeature({
   *   page: 'general',
   *   comment: 'Would love to see real-time collaboration features',
   *   customerEmail: 'user@company.com',
   *   customerOrganization: 'Acme Corp'
   * });
   * ```
   */
  async requestFeature(request: {
    page: string;
    comment: string;
    customerEmail?: string;
    customerOrganization?: string;
  }): Promise<FeedbackResponse> {
    const feedbackRequest: FeedbackRequest = {
      ...request,
      feedbackType: 'feature_request',
      category: 'enhancement'
    };

    return this.submit(feedbackRequest);
  }

  /**
   * Submit rating only (quick feedback)
   * 
   * @example
   * ```typescript
   * const response = await client.feedback.rate({
   *   page: 'api-documentation',
   *   rating: 4
   * });
   * ```
   */
  async rate(request: {
    page: string;
    rating: number;
    sessionId?: string;
  }): Promise<FeedbackResponse> {
    const feedbackRequest: FeedbackRequest = {
      ...request,
      feedbackType: 'general'
    };

    return this.submit(feedbackRequest);
  }

  /**
   * Get feedback status
   * 
   * @example
   * ```typescript
   * const status = await client.feedback.getStatus('feedback_abc123');
   * console.log('Feedback status:', status);
   * ```
   */
  async getStatus(feedbackId: string): Promise<any> {
    if (!feedbackId) {
      throw new Error('Feedback ID is required');
    }

    return this.client.request('GET', `/v2/feedback/${feedbackId}`);
  }

  /**
   * Validate feedback request
   */
  private _validateFeedbackRequest(request: FeedbackRequest): void {
    if (!request.page || typeof request.page !== 'string') {
      throw new Error('Page is required and must be a string');
    }

    if (request.page.trim().length < 1) {
      throw new Error('Page must not be empty');
    }

    if (request.rating !== undefined) {
      if (typeof request.rating !== 'number' || 
          !Number.isInteger(request.rating) || 
          request.rating < 1 || 
          request.rating > 5) {
        throw new Error('Rating must be an integer between 1 and 5');
      }
    }

    if (request.comment !== undefined) {
      if (typeof request.comment !== 'string') {
        throw new Error('Comment must be a string');
      }

      if (request.comment.trim().length > 5000) {
        throw new Error('Comment must be 5000 characters or less');
      }
    }

    if (request.feedbackType !== undefined) {
      const validTypes = ['general', 'bug_report', 'feature_request', 'support'];
      if (!validTypes.includes(request.feedbackType)) {
        throw new Error(`Feedback type must be one of: ${validTypes.join(', ')}`);
      }
    }

    if (request.customerEmail !== undefined) {
      if (typeof request.customerEmail !== 'string') {
        throw new Error('Customer email must be a string');
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(request.customerEmail)) {
        throw new Error('Customer email must be a valid email address');
      }
    }

    if (request.customerOrganization !== undefined) {
      if (typeof request.customerOrganization !== 'string') {
        throw new Error('Customer organization must be a string');
      }

      if (request.customerOrganization.trim().length > 255) {
        throw new Error('Customer organization must be 255 characters or less');
      }
    }

    // Ensure either rating or comment is provided
    if (!request.rating && (!request.comment || request.comment.trim().length === 0)) {
      throw new Error('Either rating or comment must be provided');
    }
  }
}
